import { createClient } from "@supabase/supabase-js";
/*
Supabase integration helper
- Uses environment variables REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY
- If not configured, falls back to demo mode (localStorage persistence)
*/

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ??
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhnZHh6bHR5cXZoc2trbW9hdm5mIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjMwOTMxMTgsImV4cCI6MjA3ODY2OTExOH0.b0dH79CnqykLYylBn7ymZuFzSjoyz5wmZXar_jFtxq0";

// For demo purposes, create a mock client if envs are missing
const isDemo = !supabaseUrl || !supabaseAnonKey;

export const supabase = isDemo
  ? null
  : createClient(supabaseUrl, supabaseAnonKey);

// Database table schemas (for reference)
export const TABLES = {
  QUOTE_REQUESTS: "quote_requests",
  LOCATIONS: "locations",
  ORDERS: "orders",
};

// Helper functions for database operations
export const supabaseHelpers = {
  // Save location data
  async saveLocation(locationData) {
    if (!supabase) {
      // Demo mode - just log the data and return mock success
      console.log("Demo mode - Location data saved:", locationData);
      return { success: true, id: Math.random().toString(36).substr(2, 9) };
    }
    const user = (await supabase.auth.getUser()).data.user;
    const userId = user?.id || locationData.user_id || null;

    const { data, error } = await supabase
      .from(TABLES.LOCATIONS)
      .insert([
        {
          user_id: userId,
          latitude: locationData.latitude,
          longitude: locationData.longitude,
          address: locationData.address,
          city: locationData.city,
          state: locationData.state,
          zip_code: locationData.zip_code,
          timestamp: locationData.timestamp,
          created_at: new Date().toISOString(),
        },
      ])
      .select();

    if (error) {
      console.error("Error saving location:", error);
      throw error;
    }
    return data;
  },

  // Submit quote request
  async submitQuoteRequest(quoteData) {
    if (!supabase) {
      // Demo mode - return mock successful submission
      console.log("Demo mode - Quote request submitted:", quoteData);
      return [
        {
          id: Math.random().toString(36).substr(2, 9),
          ...quoteData,
          status: "submitted",
          created_at: new Date().toISOString(),
        },
      ];
    }
    const user = (await supabase.auth.getUser()).data.user;
    const userId = user?.id || quoteData.user_id || null;

    const { data, error } = await supabase
      .from(TABLES.QUOTE_REQUESTS)
      .insert([
        {
          user_id: userId,
          property_type: quoteData.property_type,
          roofing_type: quoteData.roofing_type,
          project_type: quoteData.project_type,
          urgency: quoteData.urgency,
          full_name: quoteData.full_name,
          email: quoteData.email,
          phone: quoteData.phone,
          address: quoteData.address,
          latitude: quoteData.latitude,
          longitude: quoteData.longitude,
          city: quoteData.city,
          state: quoteData.state,
          zip_code: quoteData.zip_code,
          additional_notes: quoteData.additional_notes,
          photos: quoteData.photos || [],
          status: "submitted",
          timestamp: quoteData.timestamp,
          created_at: new Date().toISOString(),
        },
      ])
      .select();

    if (error) {
      console.error("Error submitting quote request:", error);
      throw error;
    }
    return data;
  },

  // Get all quote requests (admin/debug)
  async getAllQuoteRequests() {
    if (!supabase) {
      // Demo mode - return mock data
      return [];
    }

    const { data, error } = await supabase
      .from(TABLES.QUOTE_REQUESTS)
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching quote requests:", error);
      throw error;
    }
    return data;
  },

  // Get quote requests by current user
  async getMyQuoteRequests() {
    if (!supabase) {
      return [];
    }
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from(TABLES.QUOTE_REQUESTS)
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching user quote requests:", error);
      throw error;
    }
    return data;
  },

  // Save order to database
  async saveOrder(orderData) {
    if (!supabase) {
      // Demo mode - save to localStorage
      console.log("Demo mode - Order data saved:", orderData);
      const existingOrders = JSON.parse(localStorage.getItem("orders") || "[]");
      const newOrder = {
        id:
          "ORD-" +
          new Date().getFullYear() +
          "-" +
          String(existingOrders.length + 1).padStart(3, "0"),
        ...orderData,
        created_at: new Date().toISOString(),
      };
      existingOrders.push(newOrder);
      localStorage.setItem("orders", JSON.stringify(existingOrders));
      return { success: true, data: newOrder, error: null };
    }
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return {
        success: false,
        data: null,
        error: { message: "Not authenticated" },
      };
    }

    const payload = [
      {
        user_id: user.id,
        user_email: user.email,
        order_date: orderData.order_date,
        status: orderData.status || "pending",
        homeowner_name: orderData.homeowner_name,
        homeowner_email: orderData.homeowner_email,
        homeowner_phone: orderData.homeowner_phone,
        property_address: orderData.property_address,
        property_type: orderData.property_type,
        project_type: orderData.project_type,
        contractor_name: orderData.contractor_name,
        contractor_contact: orderData.contractor_contact,
        contractor_phone: orderData.contractor_phone,
        contractor_email: orderData.contractor_email,
        contractor_address: orderData.contractor_address,
        contractor_rating: orderData.contractor_rating,
        contractor_avatar: orderData.contractor_avatar,
        total_cost: orderData.total_cost,
        timeline: orderData.timeline,
        warranty: orderData.warranty,
        materials: orderData.materials,
        scheduled_date: orderData.scheduled_date,
        preferred_time: orderData.preferred_time,
        alternate_date: orderData.alternate_date,
        alternate_time: orderData.alternate_time,
        completion_date: orderData.completion_date,
        roof_measurements: orderData.roof_measurements,
        contact_method: orderData.contact_method,
        special_instructions: orderData.special_instructions,
        access_instructions: orderData.access_instructions,
        emergency_contact: orderData.emergency_contact,
        payment_method: orderData.payment_method,
        bid_details: orderData.bid_details,
        created_at: new Date().toISOString(),
      },
    ];

    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .insert(payload)
      .select();

    if (error) {
      console.error("Error saving order:", error);
      return { success: false, data: null, error };
    }
    return { success: true, data: data[0], error: null };
  },

  // Get current user's orders only (RLS-secure)
  async getMyOrders() {
    if (!supabase) {
      // Demo mode - get from localStorage
      return JSON.parse(localStorage.getItem("orders") || "[]");
    }
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching my orders:", error);
      throw error;
    }
    return data;
  },

  // Admin/debug only: get all orders (requires permissive policies)
  async getAllOrders() {
    if (!supabase) {
      // Demo mode - get from localStorage
      return JSON.parse(localStorage.getItem("orders") || "[]");
    }

    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching orders:", error);
      throw error;
    }
    return data;
  },

  // Update order status
  async updateOrderStatus(orderId, status) {
    if (!supabase) {
      // Demo mode - update localStorage
      const orders = JSON.parse(localStorage.getItem("orders") || "[]");
      const updatedOrders = orders.map((order) =>
        order.id === orderId ? { ...order, status } : order
      );
      localStorage.setItem("orders", JSON.stringify(updatedOrders));
      return { success: true, error: null };
    }

    const { data, error } = await supabase
      .from(TABLES.ORDERS)
      .update({ status, updated_at: new Date().toISOString() })
      .eq("id", orderId)
      .select();

    if (error) {
      console.error("Error updating order status:", error);
      return { success: false, error };
    }
    return { success: true, data: data[0], error: null };
  },
};
